/*
 * Copyright (c) 2020 General Electric Company. All rights reserved.
 *
 * The copyright to the computer software herein is the property of
 * General Electric Company. The software may be used and/or copied only
 * with the written permission of General Electric Company or in accordance
 * with the terms and conditions stipulated in the agreement/contract
 * under which the software has been supplied.
 */

import { NavigatorList } from './navigator-list';
import { WorkList } from '../worklist-navigator/work-list/work-list';

export class ToDoList extends NavigatorList {
  isPinned: boolean;
  endPointId: string;
  sortByPriority: boolean;
  worklistId: string;

  [key: string]: any;

  constructor(workList: WorkList, config: any) {
    super('todo-list', {
      isFilterable: true,
      isSortable: true
    });
    this.id = config.id;
    this.pageSize = config.pageSize;
    this.displayName = config.displayName;
    this.isFilterable = true;
    this.isSortable = true;
    this.endPointId = workList.id.toString();
    this.worklistId = this.endPointId;
    this.isPinned = workList.pinned;
    this.sortByPriority = workList.sortByPriority;
    this.isToDo = true;
    this.isHidden = false;

    this.setParamsByToDoWorkList(config);
  }

  buildParams(config: any): any {
    const params = {} as any;
    switch (this.id) {
      case ToDoListId.ASSIGNED: {
        params.filter = {
          actorName: config.currentUser,
          actionCode: 'ASSIGN'
        };
        if (config.currentContextPtId && config.currentPatientGroupingIndex) {
          params.currentContextPatientId = config.currentContextPtId;
          params.currentPatientGroupingIndex = config.currentPatientGroupingIndex;
        }
        break;
      }
      case ToDoListId.UNSIGNED: {
        let unsignedFilterCode;
        if (config.unsignedFilter === 'transcribed') {
          unsignedFilterCode = 70;
        } else if (config.unsignedFilter === 'dictated') {
          unsignedFilterCode = 60;
        }

        params.reportStateWorklistId = this.worklistId;
        params.actorName = config.currentUser;
        params.examStatus = unsignedFilterCode;

        if (config.currentContextPtId && config.currentPatientGroupingIndex) {
          params.currentContextPatientId = config.currentContextPtId;
          params.currentPatientGroupingIndex = config.currentPatientGroupingIndex;
        }

        if (config.ID_AttendingReviewWL && config.attendingReview === 'true') {
          params.isAttendingRead = false;
        }
        break;
      }
      case ToDoListId.ATTENDING: {
        params.attendingReadWorklistId = this.worklistId;
        params.actorName = config.currentUser;
        params.attendingReadExamStatus = 70;
        params.filter = this.filterData;
        if (config.currentContextPtId && config.currentPatientGroupingIndex) {
          params.currentContextPatientId = config.currentContextPtId;
          params.currentPatientGroupingIndex = config.currentPatientGroupingIndex;
        }
        break;
      }
      case ToDoListId.RECENTLY_VIEWED: {
        params.filter = {
          worklistItemDescriptorUserName: config.currentUser,
          worklistItemDescriptorWorklistId: this.endPointId
        };
        break;
      }
      case ToDoListId.TAB: {
        params.actorName = config.currentUser;
        params.filter = this.filter;
        if (config.currentContextPtId) {
          params.currentContextPatientId = config.currentContextPtId;
        }
        break;
      }
      case ToDoListId.EXCEPTION: {
        params.actorName = config.currentUser;
        params.filter = this.filter;
        if (config.currentContextPtId) {
          params.currentContextPatientId = config.currentContextPtId;
        }
        break;
      }
      case ToDoListId.NAW_COREPORT:
      case ToDoListId.NAW_REVIEWREQUESTED: {
        params.filter = {
          actorName: config.currentUser
        };
        break;
      }
    }
    return params;
  }

  getEmptyMessage(): string | any {
    let msg;
    switch (this.id) {
      case ToDoListId.ASSIGNED: {
        msg = 'assignments.noExamsAssignedMsg';
        break;
      }
      case ToDoListId.RECENTLY_VIEWED: {
        if (this.isFiltered) {
          msg = 'workloadManager.navigator.emptyFilterMsg';
        } else {
          msg = 'recentExams.noRecentExamsMsg';
        }
        break;
      }
      case ToDoListId.TAB: {
        msg = 'tabWorklist.noExamsdMsg';
        break;
      }
      default: {
        msg = 'workloadManager.listEmptyMsg';
        break;
      }
    }
    return msg;
  }

  private setParamsByToDoWorkList(config: any) {
    if (this.id === ToDoListId.RECENTLY_VIEWED) {
      this.filter = {
        worklistItemDescriptorUserName: config.currentUser,
        worklistItemDescriptorWorklistId: this.endPointId
      };
      this.isClickable = true;
      this.isCollapsible = false;
    } else if (this.id === ToDoListId.UNSIGNED) {
      this.endPointId = ToDoListId.UNSIGNED;
      this.pageSize = NaN;
      this.preference = { key: 'viewer' };
    } else if (this.id === ToDoListId.ATTENDING) {
      this.endPointId = 'attendingRead';
      this.pageSize = NaN;
    } else if (this.id === ToDoListId.TAB) {
      this.filter = {
        worklistItemDescriptorUserName: config.currentUser,
        worklistItemDescriptorWorklistId: this.worklistId
      };
    }
  }
}

export enum ToDoListId {
  ASSIGNED = 'assigned',
  UNSIGNED = 'unsigned',
  TAB = 'tabWorklist',
  ATTENDING = 'attendingread',
  RECENTLY_VIEWED = 'recently-viewed',
  EXCEPTION = 'exceptionWorklist',
  NAW_COREPORT = 'nawCoreport',
  NAW_REVIEWREQUESTED = 'nawReviewRequested'
}
